import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useAppointment } from "@/features/appointments/hooks/appointmentQueries";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { usePaymentSummary } from "../hooks/paymentQueries";
import type { PaymentSummary } from "../types/paymentTypes";

export interface AppointmentChargeContext {
  appointmentId: string;
  appointment: Appointment;
  summary: PaymentSummary;
  businessId: string;
  userId: string;
}

/** Shared owner/loading/active-payment gate for the appointment charge steps. */
export function AppointmentChargeGuard({ title, children }: { title: string; children: (context: AppointmentChargeContext) => ReactNode }) {
  const { appointmentId = "" } = useParams();
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const appointment = useAppointment(appointmentId);
  const summary = usePaymentSummary(appointmentId);
  const { goToAppointmentPayment } = useAppNavigation();
  const authorized = !!businessId && !!user && business?.ownerId === user.id;
  return <Page>
    <PageHeader title={title} />
    <PageContent><div className="mx-auto max-w-2xl space-y-4">
      {!authorized ? <ErrorState message="Only the business owner can charge an appointment." /> :
        appointment.isPending || summary.isPending ? <RowSkeleton /> :
          appointment.error || summary.error ? <ErrorState message={appointment.error?.message ?? summary.error!.message}
            onRetry={() => { void appointment.refetch(); void summary.refetch(); }} /> :
            !appointment.data ? <ErrorState message="Appointment not found." /> :
              summary.data.activePayment ? <>
                <p>This appointment already has an unresolved card payment. Resume or cancel it before taking another payment.</p>
                <Button onClick={() => goToAppointmentPayment(appointmentId, summary.data.activePayment!.id)}>Resume payment</Button>
              </> : children({ appointmentId, appointment: appointment.data, summary: summary.data, businessId, userId: user.id })}
    </div></PageContent>
  </Page>;
}
